import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { get, type Tutor } from "../api.js";
import { staffBooking } from "./staff.js";
import { STUDENT_CREDIT_STATE_CHANGED_EVENT, studentCreditStateChangedFor } from "./studentCreditEvents.js";

type BlockOptions = Parameters<typeof staffBooking.lessonBlocks>[1];

function createStudentCreditReads() {
  const pending = new Map<string, Promise<unknown>>();
  const listeners = new Set<() => void>();
  let revision = 0;
  function read<T>(key: string, load: () => Promise<T>): Promise<T> {
    const previous = pending.get(key);
    if (previous) return previous as Promise<T>;
    const request = load().catch((cause: unknown) => {
      if (pending.get(key) === request) pending.delete(key);
      throw cause;
    });
    pending.set(key, request);
    return request;
  }
  return {
    lessonCredits: (studentId: string) => read(`credits:${studentId}`, () => staffBooking.lessonCredits(studentId)),
    lessonBlocks: (studentId: string, options: BlockOptions = {}) => read(
      `blocks:${studentId}:${options.view ?? "all"}:${options.limit ?? 100}:${options.offset ?? 0}`,
      () => staffBooking.lessonBlocks(studentId, options),
    ),
    tutors: () => read("tutors", () => get<{ tutors: Tutor[] }>("/admin/tutors")),
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    getRevision: () => revision,
    invalidate: () => {
      for (const key of pending.keys()) if (key !== "tutors") pending.delete(key);
      revision += 1;
      for (const listener of listeners) listener();
    },
  };
}

type StudentCreditReads = ReturnType<typeof createStudentCreditReads>;
const Context = createContext<StudentCreditReads | null>(null);
const uncachedReads: StudentCreditReads = {
  lessonCredits: (studentId) => staffBooking.lessonCredits(studentId),
  lessonBlocks: (studentId, options) => staffBooking.lessonBlocks(studentId, options),
  tutors: () => get<{ tutors: Tutor[] }>("/admin/tutors"),
  subscribe: () => () => undefined,
  getRevision: () => 0,
  invalidate: () => undefined,
};

/** Per-record cache: shared reads and invalidation, never persisted across accounts. */
export function StudentCreditReadProvider({ studentId, children }: { studentId: string; children: ReactNode }) {
  const reads = useMemo(createStudentCreditReads, [studentId]);
  useEffect(() => {
    const changed = (event: Event) => {
      if (studentCreditStateChangedFor(event, studentId)) reads.invalidate();
    };
    window.addEventListener(STUDENT_CREDIT_STATE_CHANGED_EVENT, changed);
    return () => window.removeEventListener(STUDENT_CREDIT_STATE_CHANGED_EVENT, changed);
  }, [reads, studentId]);
  return <Context.Provider value={reads}>{children}</Context.Provider>;
}

export function useStudentCreditReads(): StudentCreditReads & { revision: number } {
  const reads = useContext(Context) ?? uncachedReads;
  const revision = useSyncExternalStore(reads.subscribe, reads.getRevision, reads.getRevision);
  return useMemo(() => ({ ...reads, revision }), [reads, revision]);
}
